import { createContext, useContext, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

export type NavDirection = "push" | "pop" | "tab" | "none";

export interface NavigationDirectionValue {
  readonly direction: NavDirection;
  readonly pageKey: string;
}

const NavigationDirectionContext = createContext<NavigationDirectionValue>({
  direction: "none",
  pageKey: "",
});

export function useNavigationDirection() {
  return useContext(NavigationDirectionContext);
}

export function NavigationDirectionProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const [direction, setDirection] = useState<NavDirection>("none");
  const [pageKey, setPageKey] = useState(location.pathname);

  const mainRoutes = new Set(["/", "/topics", "/treehole", "/profile"]);

  const getDirection = useCallback(
    (from: string, to: string): NavDirection => {
      if (from === to) return "none";

      const isFromMain = mainRoutes.has(from);
      const isToMain = mainRoutes.has(to);

      // Tab switch between main routes → crossfade
      if (isFromMain && isToMain) return "tab";

      // Navigating from main to detail → push
      if (isFromMain && !isToMain) return "push";

      // Navigating from detail back to main → pop
      if (!isFromMain && isToMain) return "pop";

      // Detail to detail → push if depth increases
      const fromSegments = from.split("/").filter(Boolean);
      const toSegments = to.split("/").filter(Boolean);
      if (toSegments.length >= fromSegments.length) return "push";

      return "pop";
    },
    [],
  );

  // React to path changes
  if (prevPathRef.current !== location.pathname) {
    const newDirection = getDirection(prevPathRef.current, location.pathname);
    prevPathRef.current = location.pathname;
    // Use queueMicrotask to avoid setState during render
    queueMicrotask(() => {
      setDirection(newDirection);
      setPageKey(location.pathname);
    });
  }

  return (
    <NavigationDirectionContext.Provider value={{ direction, pageKey }}>
      {children}
    </NavigationDirectionContext.Provider>
  );
}
